'use client';

import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import Layout from '../Layout';
import UnmappedAlert from '../../components/UnmappedAlert.js';
import PendingInvite from '../Settings/PendingInvites';
import {
  CircleStackIcon,
  ChartBarIcon,
  FolderIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

import gaugeImage from '../../assets/380Plus.png';

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const [isMemberOfOrganization, setIsMemberOfOrganization] = useState(true);
  const [metrics, setMetrics] = useState({
    totalMeasurements: 0,
    newMeasurementsLastWeek: 0,
    totalProjects: 0,
  });
  const [loading, setLoading] = useState(true);

  // Accordion state for best practices
  const [openIndex, setOpenIndex] = useState(null);
  const toggleAccordion = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  // Example best practices data
  const bestPractices = [
    {
      title: 'Invite Your Team Members',
      content:
        'Go to Settings → Team. You can invite them by email or send them the Company Key to join your organization directly.',
      link: '/settings/Team',
    },
    {
      title: 'Create a Project',
      content:
        'Go to Unmapped Measurements. Select a measurement to be used as the Master Point, then click “Actions → Create Project.” The page will then refresh and detect all other measurements that fit into the new Project GeoFence.',
      link: '/unmappedproject',
      },
    {
      title: 'Send Measurements to OnStation',
      content:
        'First, Configure the OnStation Integration on the Integrations tab.  Then, open a Project, select Measurements and click “Actions → Send to OnStation.”',
      link: '/settings/integrations',
      },
  ];

  useEffect(() => {
    document.title = 'Dashboard | TransTech Connect';
    return () => {
      document.title = 'TransTech Connect';
    };
  }, []);

  useEffect(() => {
    if (!user) return;
  
    const fetchMetrics = async () => {
      if (!user?.id) return;
      try {
        // Fetch total measurements for the user
        const { data: totalMeasurements } = await supabase
          .from('measurements')
          .select('id')
          .eq('profile_id', user.id);
  
        // Fetch measurements added in the last 7 days
        const { data: newMeasurementsLastWeek } = await supabase
          .from('measurements')
          .select('id')
          .eq('profile_id', user.id)
          .gte(
            'created_at',
            new Date(new Date() - 7 * 24 * 60 * 60 * 1000).toISOString()
          );
  
        // Fetch the user's organization membership
        const { data: organizationMember, error: orgError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
  
        let totalProjects = 0;
        if (!orgError && organizationMember) {
          // Query the projects table using the organization_id from the membership
          const { data: projectsData, error: projectError } = await supabase
            .from('projects')
            .select('id')
            .eq('organization_id', organizationMember.organization_id);
          if (projectError) {
            console.error('Error fetching projects:', projectError);
          }
          totalProjects = projectsData?.length || 0;
        }
  
        setMetrics({
          totalMeasurements: totalMeasurements?.length || 0,
          newMeasurementsLastWeek: newMeasurementsLastWeek?.length || 0,
          totalProjects,
        });
      } catch (error) {
        console.error('Error fetching metrics:', error);
      }
    };
  

    const checkOrganizationMembership = async () => {
      if (!user?.id) return;
      try {
        const { data: organizationMember, error } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (error || !organizationMember) {
          setIsMemberOfOrganization(false);
        } else {
          setIsMemberOfOrganization(true);
        }
      } catch (err) {
        console.error('Error checking organization membership:', err);
        setIsMemberOfOrganization(false);
      } finally {
        setLoading(false);
      }
    };

    checkOrganizationMembership();
    fetchMetrics();
  }, [user]);

  if (!user) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-lg font-medium leading-6 text-gray-900">Loading...</h2>
        </div>
      </Layout>
    );
  }

  const metricCards = [
    {
      title: 'Total Measurements',
      subtitle: 'Measurements logged',
      value: metrics.totalMeasurements,
      icon: <CircleStackIcon className="h-6 w-6 text-gray-500" />,
    },
    {
      title: 'Measurements (Last 7 Days)',
      subtitle: 'Recently added',
      value: metrics.newMeasurementsLastWeek,
      icon: <ChartBarIcon className="h-6 w-6 text-gray-500" />,
    },
    {
      title: 'Total Projects',
      subtitle: 'Projects tracked',
      value: metrics.totalProjects,
      icon: <FolderIcon className="h-6 w-6 text-gray-500" />,
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto flex flex-col h-full justify-between">
        {/* Top Section: Header and Metrics */}
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-medium leading-6 text-gray-900">
              Dashboard
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Statistics for your activity and projects.
            </p>
          </div>

          {/* Organization Warning */}
          {!loading && !isMemberOfOrganization && (
            <div className="border-l-4 border-red-400 bg-red-50 p-4 mt-4">
              <div className="flex">
                <div className="shrink-0">
                  <ExclamationTriangleIcon
                    aria-hidden="true"
                    className="h-5 w-5 text-red-400"
                  />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    You are not a member of a Company.{' '}
                    <a
                      href="/settings/Team"
                      className="font-medium text-red-700 underline hover:text-red-600"
                    >
                      Create or join an existing one here.
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pending Invites */}
          <PendingInvite userId={user.id} />

          {/* Metric Cards */}
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {metricCards.map((card, index) => (
              <div
                key={index}
                className="bg-white overflow-hidden shadow rounded-lg"
              >
                <div className="p-4">
                  {/* Icon and Title Row */}
                  <div className="flex items-center">
                    <div>{card.icon}</div>
                    <h3 className="ml-3 text-sm font-medium text-gray-900">
                      {card.title}
                    </h3>
                  </div>

                  {/* Subtitle */}
                  <p className="mt-2 text-sm text-gray-500">{card.subtitle}</p>

                  {/* Metric + Button Row */}
                  <div className="mt-4 flex items-center">
                    <p className="text-3xl font-bold text-gray-900">
                      {card.value}
                    </p>
                    {card.title === 'Total Measurements' && (
                      <div className="ml-auto">
                        {/* Show UnmappedAlert as a button */}
                        <UnmappedAlert variant="button" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Section: Promo Banner and Best Practices */}
        <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">

          {/* Best Practices Accordion */}
          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-3">
              Best Practices
            </h3>
            <div className="bg-white border border-gray-200 rounded-md divide-y divide-gray-200">
              {bestPractices.map((item, idx) => (
                <div key={idx} className="p-4">
                  {/* Accordion Header */}
                  <button
                    onClick={() => toggleAccordion(idx)}
                    className="flex w-full items-center justify-between focus:outline-none"
                  >
                    <span className="font-medium text-gray-800">{item.title}</span>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-gray-500 transition-transform ${
                        openIndex === idx ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {/* Accordion Content */}
                  {openIndex === idx && (
                    <div className="mt-2 text-sm text-gray-600">
                      <p>{item.content}</p>
                      {item.link && (
                        <a
                          href={item.link}
                          className="inline-block text-yellow-600 underline mt-2"
                        >
                          Show me
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Promo Banner */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 flex flex-col sm:flex-row items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <img
                  src={gaugeImage}
                  alt="PQI380+ gauge"
                  className="h-24 w-auto hidden sm:block"
                />
              </div>
              <div className="mt-4 sm:mt-0">
                <h3 className="text-lg font-bold text-yellow-700">
                  Introducing the NEW PQI 380+ Gauge!
                </h3>
                <p className="text-sm text-yellow-800 mt-1">
                The keystone of TransTech Connect, faster measurements, real-time data.
                </p>
              </div>
            </div>
            <button className="mt-4 sm:mt-0 flex items-center gap-2 px-4 py-2 bg-yellow-600/20 text-sm text-yellow-800 border-2 border-yellow-600 rounded-md hover:bg-yellow-600 hover:text-white transition-colors duration-300"
            onClick={() => {
              window.open('https://transtechsys.com/products/pqi380Plus', '_blank');
            }}>
              Contact Sales today
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>

          
        </div>
      </div>
    </Layout>
  );
}
